import type { Toast as PrimeToast } from "primereact/toast";
import { useMemo, useRef } from "react";
import type { ReactNode } from "react";

import { useStableRest } from "../../../../utils/use-stable-rest";
import type {
  ToastApi,
  ToastOptions,
  ToastSeverity,
  ToastShortcutOptions,
} from "../../types-toast";
import type {
  ToastProviderAttributes,
  ToastProviderViewModel,
  UseToastProviderOptions,
} from "./types-provider";

/** Duração padrão por severity (as telas usam 3000 para sucesso e 5000 para erro). */
export const DEFAULT_TOAST_LIFE: Record<ToastSeverity, number> = {
  success: 3000,
  info: 3000,
  warn: 5000,
  error: 5000,
  secondary: 3000,
  contrast: 3000,
};

/** Duração efetiva: o `life` do toast vence o do provider, que vence o padrão da severity. */
export function resolveToastLife(
  severity: ToastSeverity,
  own: number | undefined,
  provider: UseToastProviderOptions["life"],
): number {
  if (own !== undefined) return own;
  if (typeof provider === "number") return provider;
  return provider?.[severity] ?? DEFAULT_TOAST_LIFE[severity];
}

/** ViewModel do ToastProvider: ref do `Toast` do Prime e a API estável de `useToast`; sem JSX. */
export function useToastProvider({
  position = "top-right",
  life,
  className,
  style,
  ...props
}: UseToastProviderOptions & ToastProviderAttributes = {}): ToastProviderViewModel {
  const rest = useStableRest(props);
  const toastRef = useRef<PrimeToast>(null);

  const api = useMemo<ToastApi>(() => {
    const message = ({ severity, summary, detail, life: own, sticky }: ToastOptions) => ({
      severity,
      summary,
      detail,
      sticky,
      life: sticky ? undefined : resolveToastLife(severity, own, life),
    });
    const show = (options: ToastOptions) => toastRef.current?.show(message(options));
    const shortcut =
      (severity: ToastSeverity) =>
      (summary: string, detail?: ReactNode, options?: ToastShortcutOptions) =>
        show({ ...options, severity, summary, detail });
    return {
      show,
      replace: (options) => toastRef.current?.replace(message(options)),
      success: shortcut("success"),
      info: shortcut("info"),
      warn: shortcut("warn"),
      error: shortcut("error"),
      secondary: shortcut("secondary"),
      contrast: shortcut("contrast"),
      clear: () => toastRef.current?.clear(),
    };
  }, [life]);

  return { toastRef, api, position, className, style, rest };
}
